'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, MB, OL, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/frontier-extra'

const RAGPipeline = dynamic(() => import('../_components/widgets/FrontierExtraWidgets').then(m => m.RAGPipeline), { ssr: false })

export default function RetrievalAndRAG() {
  return (
    <Lesson>
      <Step>
        <H>Open-book answers</H>
        <P>
          A language model only knows what was in its training data. It doesn’t know your company’s documents, yesterday’s news,
          or anything after its training cutoff. Asked anyway, it may <strong>hallucinate</strong>: produce a fluent, confident,
          wrong answer.
        </P>
        <P>
          <strong>Retrieval-augmented generation</strong> (RAG) turns the exam into an open-book one:
        </P>
        <OL>
          <li>
            <strong>Retrieve</strong>: find the passages most relevant to the question.
          </li>
          <li>
            <strong>Augment</strong>: paste them into the prompt, with an instruction to use them.
          </li>
          <li>
            <strong>Generate</strong>: the model answers, citing what it was given.
          </li>
        </OL>
        <Figure n="10.4" wide caption="Type any question. The pipeline scores all 14 documents against it, keeps the best 3, and builds the prompt the model would see.">
          <RAGPipeline />
        </Figure>
      </Step>

      <Step gate="chunking">
        <H>Step 0: chop the library</H>
        <P>
          Real documents are long, and prompts have limited room. So documents are split into <strong>chunks</strong> of a few
          hundred words, with some overlap, so a sentence cut at one boundary still appears whole in the neighbouring chunk.
        </P>
        <Exercise
          id="chunking"
          title="chunk"
          {...EX.chunking}
          hints={[
            <>
              <C>{'const step = size - overlap'}</C>; loop <C>{'for (let start = 0; ; start += step)'}</C>, push{' '}
              <C>{'words.slice(start, start + size)'}</C>, and <C>break</C> once <C>{'start + size >= words.length'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="tfidf-search">
        <H>Step 1: search</H>
        <P>
          The classic way to score relevance is <strong>TF-IDF</strong>: a word matters if it appears often in this document
          (term frequency) and rarely elsewhere (inverse document frequency). “Photosynthesis” is strong evidence; “the” is
          none. Each document becomes a vector of weights, and relevance is the cosine similarity from Module 3.
        </P>
        <MB>{'\\text{weight}(w, d) = \\text{count}(w, d) \\times \\Big(\\log\\frac{N + 1}{\\text{df}(w) + 1} + 1\\Big)'}</MB>
        <Exercise
          id="tfidf-search"
          title="idf, vectorize, cosine, search"
          {...EX.tfidfSearch}
          hints={[
            <>
              <C>{'const df = docWords.filter(ws => ws.includes(word)).length'}</C>
            </>,
            <>
              vectorize: count each word in a Map, then multiply each count by <C>idf(w)</C>.
            </>,
            <>
              search: <C>{'range(docs.length).sort((i, j) => cosine(q, docVecs[j]) - cosine(q, docVecs[i])).slice(0, k)'}</C>
            </>,
          ]}
        />
        <Callout kind="note">
          Production systems usually search with <strong>embeddings</strong> instead: a neural encoder (Module 9) turns every
          chunk and question into a vector, so “car” can match “automobile” with no shared words. Many combine both kinds of
          search. The rest of the pipeline is the same.
        </Callout>
      </Step>

      <Step gate={['rag-prompt', 'q-rag']}>
        <H>Steps 2 and 3: build the prompt, measure retrieval</H>
        <Exercise
          id="rag-prompt"
          title="buildPrompt and recallAtK"
          {...EX.ragPrompt}
          hints={[
            <>
              Start a list of lines with the instruction, add <C>{'`[${i + 1}] ${s.title}: ${s.text}`'}</C> for each source, then
              the question. <C>{'lines.join("\\n")'}</C>.
            </>,
            <>
              <C>{'results.filter((r, i) => r.includes(relevant[i])).length / results.length'}</C>
            </>,
          ]}
        />
        <Quiz
          id="q-rag"
          question="A RAG system gives a wrong answer. The first thing to check is…"
          options={[
            { label: 'Whether the right passage was retrieved at all', correct: true },
            { label: 'Whether the model is big enough', why: 'If the right passage never reached the prompt, no model can use it. Check retrieval first.' },
            { label: 'Whether the prompt is polite enough', why: 'Wording matters a little. Missing evidence matters far more.' },
          ]}
          explain="Measure the two halves separately: retrieval (recall@k) and generation (are answers faithful to the sources?). Very often, the fault turns out to be retrieval."
        />
        <UL>
          <li>RAG keeps knowledge fresh without retraining: update the documents, and the answers update too.</li>
          <li>It makes answers checkable: the model can cite the passage it used.</li>
          <li>
            It doesn’t make hallucination impossible. The model can still misread or ignore a source, which is why the instruction
            “say you don’t know” matters.
          </li>
        </UL>
      </Step>
    </Lesson>
  )
}
